import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import React, { useState } from 'react';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import Helper from '../../shared/helpers/Helper';
import { ITrustpageMonitoring } from '../../shared/interfaces/ITrustpageMonitoring';
import { ITrustpageSeal } from '../../shared/interfaces/ITrustpageSeal';

interface Props {
  companyName: string;
  aboutCompanyText: string;
  score: number;
  monitorings: ITrustpageMonitoring[];
  seal: ITrustpageSeal;
}

const TrustpageSummary = ({ aboutCompanyText, score, monitorings, companyName, seal }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showFullText, setShowFullText] = useState<boolean>(false);
  const showSeal = seal && score >= 2.5;
  const visibleMonitorings = monitorings.filter((entry) => entry.platform !== 'stepstone');

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: theme.spacing(2),
          border: `1px solid ${theme.palette.divider}`,
          p: theme.spacing(2),
          width: '400px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {companyName && (
            <Typography
              variant="h6"
              sx={{ alignSelf: 'center' }}
            >
              {t('about')} {companyName}
            </Typography>
          )}
          <Divider
            flexItem
            orientation="horizontal"
            sx={{
              my: theme.spacing(1),
              borderColor: theme.palette.divider,
            }}
          />
          {showSeal && (
            <>
              <img
                src={`${process.env.REACT_APP_BASE_URL}/trustpages/seal/${seal.sealFile}`}
                style={{
                  maxHeight: '180px',
                  maxWidth: '180px',
                  alignSelf: 'center',
                }}
                alt="Seal"
              />
            </>
          )}
          {aboutCompanyText && (
            <>
              <Box sx={{ my: theme.spacing(2) }}>
                <Typography variant="body2">
                  {showFullText ? aboutCompanyText : Helper.shortText(aboutCompanyText, 200)}
                </Typography>
                {aboutCompanyText.length > 200 && (
                  <Link
                    variant="body1"
                    onClick={() => setShowFullText((prevState) => !prevState)}
                    sx={{
                      my: theme.spacing(1),
                      cursor: 'pointer',
                    }}
                  >
                    {showFullText ? `${t('show_less')}` : `${t('show_more')}`}
                  </Link>
                )}
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center' }}
          >
            {t('platforms')}
          </Typography>
          <Box sx={{ display: 'flex', mt: theme.spacing(1) }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, flex: 1 }}
            >
              {t('platform')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, flex: 1 }}
            >
              {t('score')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, flex: 1 }}
            >
              {t('nav_reviews')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr', // 3 equal columns
            }}
          >
            {visibleMonitorings.map((entry, index) => (
              <React.Fragment key={index}>
                <Divider
                  flexItem
                  orientation="horizontal"
                  sx={{
                    gridColumn: '1 / -1',
                    my: theme.spacing(1),
                    borderColor: theme.palette.divider,
                  }}
                />
                <Box
                  sx={{
                    display: 'contents',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={constants.platform_logos[entry.platform]}
                      alt={`${entry.platform} Logo`}
                      style={{ maxHeight: '24px', maxWidth: '120px' }}
                    />
                  </Box>
                  <StarRating
                    value={entry.score}
                    size="small"
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {entry.reviewCount}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TrustpageSummary;
